import React from 'react';
import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useStore } from '../lib/store';
import { useCurrency } from '../hooks/useCurrency';
import { patientService, doctorService, appointmentService, invoiceService } from '../lib/services';
import { api, USE_MOCK } from '../lib/api';
import { prescriptionService } from '../modules/consultation/api/prescriptions';
import { orderService } from '../modules/consultation/api/orders';
import { encounterService } from '../modules/consultation/api/encounters';
import { dispenseService } from '../modules/pharmacy/api/dispense';
import { stockService } from '../modules/pharmacy/api/stock';
import { isDoctor, getDoctorIdFromUser } from '../lib/rbac';
import { 
  Users, 
  Calendar, 
  CreditCard, 
  TrendingUp, 
  Activity,
  Clock,
  AlertCircle,
  CheckCircle,
  XCircle,
  FileText,
  Package,
  Download,
  Stethoscope,
  Plus,
  Play,
  Eye,
  Printer,
  ArrowUpRight,
  DollarSign,
  Receipt,
  Coins,
  Wallet,
  TrendingDown,
  Share2,
  MessageSquare,
  TestTube,
  Microscope,
  Banknote
} from 'lucide-react';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { exportDashboardMetrics } from '../lib/csv';
import toast from 'react-hot-toast';

const Dashboard: React.FC = () => {
  const { user, appointments, invoices, setPatients, setDoctors, setAppointments, setInvoices } = useStore();
  const { formatCurrency } = useCurrency();
  const [loading, setLoading] = useState(true);
  const [apiStatus, setApiStatus] = useState<'checking' | 'connected' | 'disconnected'>('checking');
  const [prescriptions, setPrescriptions] = useState<any[]>([]);
  const [orders, setOrders] = useState<any[]>([]);
  const [encounters, setEncounters] = useState<any[]>([]);
  const [dispenses, setDispenses] = useState<any[]>([]);
  const [stockSummary, setStockSummary] = useState<any[]>([]);

  const isDoctorRole = isDoctor(user);
  const doctorId = getDoctorIdFromUser(user);

  useEffect(() => {
    checkApiHealth();
    loadDashboardData();
  }, []);

  const checkApiHealth = async () => {
    if (USE_MOCK) {
      setApiStatus('disconnected');
      return;
    }
    
    try {
      await api.health();
      setApiStatus('connected');
    } catch (error) {
      console.warn('API health check failed:', error);
      setApiStatus('disconnected');
    }
  };

  const loadDashboardData = async () => {
    try {
      if (isDoctorRole && doctorId) {
        const [patientsData, doctorsData, appointmentsData, prescriptionsData, ordersData, encountersData] = await Promise.all([
          patientService.getAll(undefined, user),
          doctorService.getAll(),
          appointmentService.getAll(undefined, undefined, user),
          prescriptionService.getByDoctor(doctorId),
          orderService.getAll(),
          encounterService.getTodaysEncountersByDoctor(doctorId),
        ]);
        
        setPatients(patientsData);
        setDoctors(doctorsData);
        setAppointments(appointmentsData);
        setPrescriptions(prescriptionsData);
        setOrders(ordersData.filter((order: any) => {
          return encountersData.some((enc: any) => enc.id === order.encounterId && enc.doctorId === doctorId);
        }));
        setEncounters(encountersData);
      } else {
        const [patientsData, doctorsData, appointmentsData, invoicesData, prescriptionsData, dispensesData, stockSummaryData] = await Promise.all([
          patientService.getAll(),
          doctorService.getAll(),
          appointmentService.getAll(),
          invoiceService.getAll(),
          prescriptionService.getAll(),
          dispenseService.getTodaysDispenses(),
          stockService.getSummary(),
        ]);
        
        setPatients(patientsData);
        setDoctors(doctorsData);
        setAppointments(appointmentsData);
        setInvoices(invoicesData);
        setPrescriptions(prescriptionsData);
        setDispenses(dispensesData);
        setStockSummary(stockSummaryData);
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const todayAppointments = useMemo(() => 
    appointments.filter(apt => 
      apt.appointment_date === new Date().toISOString().split('T')[0]
    ), [appointments]
  );

  const completedAppointmentsToday = useMemo(() =>
    todayAppointments.filter(apt => apt.status === 'completed'), [todayAppointments]
  );

  const prescriptionsToday = useMemo(() => {
    const today = new Date().toISOString().split('T')[0];
    return prescriptions.filter(rx => rx.createdAt?.startsWith(today));
  }, [prescriptions]);

  const consultationsToday = useMemo(() => encounters || [], [encounters]);

  const inProgressConsultations = useMemo(() => 
    encounters.filter(enc => enc.status === 'draft'), [encounters]
  );

  const recentOrders = useMemo(() => 
    orders.slice(0, 5), [orders]
  );

  const recentPrescriptions = useMemo(() => 
    prescriptions.slice(0, 5), [prescriptions]
  );

  const dispensesToday = useMemo(() => dispenses || [], [dispenses]);

  const todaySales = useMemo(() => {
    const today = new Date().toISOString().split('T')[0];
    return invoices
      .filter(inv => inv.issue_date === today)
      .reduce((sum, inv) => sum + (inv.total_amount || 0), 0);
  }, [invoices]);

  const todayDiscount = useMemo(() => {
    const today = new Date().toISOString().split('T')[0];
    return invoices
      .filter(inv => inv.issue_date === today)
      .reduce((sum, inv) => sum + (inv.discount_amount || 0), 0);
  }, [invoices]);

  const patientDue = useMemo(() => {
    return invoices
      .filter(inv => inv.status === 'pending' || inv.status === 'partial')
      .reduce((sum, inv) => sum + (inv.balance_due || 0), 0);
  }, [invoices]);

  const totalReceived = useMemo(() => {
    const today = new Date().toISOString().split('T')[0];
    return invoices
      .filter(inv => inv.payments?.some(p => p.payment_date === today))
      .reduce((sum, inv) => {
        const todayPayments = inv.payments?.filter(p => p.payment_date === today) || [];
        return sum + todayPayments.reduce((pSum, p) => pSum + p.amount, 0);
      }, 0);
  }, [invoices]);

  const cashInHand = useMemo(() => {
    const today = new Date().toISOString().split('T')[0];
    return invoices
      .filter(inv => inv.payments?.some(p => 
        p.payment_date === today && p.payment_method === 'cash'
      ))
      .reduce((sum, inv) => {
        const cashPayments = inv.payments?.filter(p => 
          p.payment_date === today && p.payment_method === 'cash'
        ) || [];
        return sum + cashPayments.reduce((pSum, p) => pSum + p.amount, 0);
      }, 0);
  }, [invoices]);

  const lowStockItems = useMemo(() => 
    stockSummary?.filter(s => s.isLowStock) || [], [stockSummary]
  );

  const nearExpiryItems = useMemo(() => 
    stockSummary?.filter(s => s.isNearExpiry) || [], [stockSummary]
  );

  const dashboardStats = [
    {
      label: 'NEW APPOINTMENT',
      value: todayAppointments.length.toString(),
      icon: Calendar,
      iconBg: 'bg-pink-100',
      iconColor: 'text-pink-600',
      labelColor: 'text-green-600',
      link: '/appointments'
    },
    {
      label: 'CASH MEMO/INVOICE',
      value: invoices.filter(inv => inv.issue_date === new Date().toISOString().split('T')[0]).length.toString(),
      icon: Receipt,
      iconBg: 'bg-red-100',
      iconColor: 'text-red-600',
      labelColor: 'text-red-600',
      link: '/billings/invoices'
    },
    {
      label: 'TODAY SALES:',
      value: formatCurrency(todaySales),
      icon: Coins,
      iconBg: 'bg-green-100',
      iconColor: 'text-green-600',
      labelColor: 'text-green-600',
      link: '/billings/invoices'
    },
    {
      label: 'TODAY DISCOUNT:',
      value: formatCurrency(todayDiscount),
      icon: TrendingDown,
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
      labelColor: 'text-blue-600',
      link: '/billings/invoices'
    },
    {
      label: 'PATIENT DUE:',
      value: formatCurrency(patientDue),
      icon: Wallet,
      iconBg: 'bg-orange-100',
      iconColor: 'text-orange-600',
      labelColor: 'text-red-600',
      link: '/billings/due-collection'
    },
    {
      label: 'PROFIT SHARE:',
      value: '0',
      icon: Share2,
      iconBg: 'bg-orange-100',
      iconColor: 'text-orange-600',
      labelColor: 'text-teal-600',
      link: '/reports'
    },
    {
      label: 'AVAILABLE SMS:',
      value: '0',
      subtext: 'Buy Now',
      icon: MessageSquare,
      iconBg: 'bg-yellow-100',
      iconColor: 'text-yellow-600',
      labelColor: 'text-gray-900',
      subtextColor: 'text-blue-600',
      link: '/softwarebilling/smsbuy'
    },
    {
      label: 'PATHOLOGY TESTS:',
      value: orders.filter(o => o.type === 'lab').length.toString(),
      icon: TestTube,
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
      labelColor: 'text-blue-900',
      link: '/lab'
    },
    {
      label: 'USG TESTS:',
      value: orders.filter(o => o.type === 'radiology').length.toString(),
      icon: Microscope,
      iconBg: 'bg-purple-100',
      iconColor: 'text-purple-600',
      labelColor: 'text-gray-900',
      link: '/radiology'
    },
    {
      label: 'TOTAL RECEIVED:',
      value: formatCurrency(totalReceived),
      icon: Banknote,
      iconBg: 'bg-green-100',
      iconColor: 'text-green-600',
      labelColor: 'text-green-600',
      link: '/accounts/payment-history'
    },
    {
      label: 'TOTAL EXPENSES:',
      value: formatCurrency(0),
      icon: TrendingDown,
      iconBg: 'bg-red-100',
      iconColor: 'text-red-600',
      labelColor: 'text-red-600',
      link: '/billings/expense-summary'
    },
    {
      label: 'CASH IN HAND:',
      value: formatCurrency(cashInHand),
      icon: Wallet,
      iconBg: 'bg-green-100',
      iconColor: 'text-green-600',
      labelColor: 'text-green-600',
      link: '/accounts'
    },
  ];

  const handleExportCSV = () => {
    exportDashboardMetrics(dashboardStats as any);
    toast.success('Dashboard metrics exported to CSV');
  };

  const recentAppointments = useMemo(() => todayAppointments.slice(0, 4), [todayAppointments]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div 
      className="space-y-6 min-h-screen"
      style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        backgroundImage: `
          url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1440 320'%3E%3Cpath fill='%23ffffff' fill-opacity='0.1' d='M0,96L48,112C96,128,192,160,288,160C384,160,480,128,576,112C672,96,768,96,864,112C960,128,1056,160,1152,160C1248,160,1344,128,1392,112L1440,96L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z'%3E%3C/path%3E%3C/svg%3E"),
          url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1440 320'%3E%3Cpath fill='%23ffffff' fill-opacity='0.05' d='M0,224L48,213.3C96,203,192,181,288,181.3C384,181,480,203,576,213.3C672,224,768,224,864,213.3C960,203,1056,181,1152,181.3C1248,181,1344,203,1392,213.3L1440,224L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z'%3E%3C/path%3E%3C/svg%3E")
        `,
        backgroundPosition: 'bottom, bottom',
        backgroundRepeat: 'no-repeat, no-repeat',
        backgroundSize: 'cover, cover'
      }}
    >
      {/* Stats Grid - 6 columns matching reference design */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 pt-6 px-6">
        {dashboardStats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Link
              key={index}
              to={stat.link}
              className="bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-300 p-6 flex flex-col items-center justify-center text-center group cursor-pointer transform hover:-translate-y-1"
            >
              <div className={`${stat.iconBg} p-4 rounded-full mb-3 group-hover:scale-110 transition-transform duration-300`}>
                <Icon className={`w-8 h-8 ${stat.iconColor}`} />
              </div>
              <div className={`font-bold text-sm mb-2 ${stat.labelColor} uppercase tracking-wide`}>
                {stat.label}
              </div>
              <div className="text-2xl font-bold text-gray-900 mb-1">
                {stat.value}
              </div>
              {stat.subtext && (
                <div className={`text-sm font-medium ${stat.subtextColor || 'text-blue-600'}`}>
                  {stat.subtext}
                </div>
              )}
            </Link>
          );
        })}
      </div>

      {/* Recent Activity Section */}
      <div className="px-6 pb-6">
        {isDoctorRole ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Today's Appointments */}
          <div className="bg-white rounded-xl shadow-soft border border-neutral-200">
            <div className="p-6 border-b border-neutral-200 bg-gradient-to-r from-primary-50 to-white">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-neutral-900">Today's Appointments</h2>
                <Clock className="w-5 h-5 text-primary-600" />
              </div>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {recentAppointments.map((appointment) => (
                  <div key={appointment.id} className="flex items-center justify-between p-3 bg-gradient-to-r from-neutral-50 to-white rounded-lg border border-neutral-200 hover:shadow-sm transition-all">
                    <div>
                      <p className="font-medium text-neutral-900">{appointment.patient?.name}</p>
                      <p className="text-sm text-neutral-600">{appointment.start_time}</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        appointment.status === 'confirmed' 
                          ? 'bg-success-100 text-success-800'
                          : appointment.status === 'scheduled'
                          ? 'bg-warning-100 text-warning-800'
                          : 'bg-error-100 text-error-800'
                      }`}>
                        {appointment.status}
                      </span>
                      <Link
                        to={`/consultations/new?appointmentId=${appointment.id}`}
                        className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                      >
                        Start
                      </Link>
                    </div>
                  </div>
                ))}
                {recentAppointments.length === 0 && (
                  <p className="text-neutral-500 text-center py-4">No appointments scheduled for today</p>
                )}
              </div>
            </div>
          </div>

          {/* In-progress Consultations */}
          <div className="bg-white rounded-xl shadow-soft border border-neutral-200">
            <div className="p-6 border-b border-neutral-200 bg-gradient-to-r from-primary-50 to-white">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-neutral-900">In-progress Consultations</h2>
                <Stethoscope className="w-5 h-5 text-primary-600" />
              </div>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {inProgressConsultations.map((consultation) => (
                  <div key={consultation.id} className="flex items-center justify-between p-3 bg-gradient-to-r from-neutral-50 to-white rounded-lg border border-neutral-200 hover:shadow-sm transition-all">
                    <div>
                      <p className="font-medium text-neutral-900">Patient {consultation.patientId}</p>
                      <p className="text-sm text-neutral-600">{new Date(consultation.dateTime).toLocaleTimeString()}</p>
                    </div>
                    <Link
                      to={`/consultations/${consultation.id}`}
                      className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                    >
                      Resume
                    </Link>
                  </div>
                ))}
                {inProgressConsultations.length === 0 && (
                  <p className="text-neutral-500 text-center py-4">No consultations in progress</p>
                )}
              </div>
            </div>
          </div>

          {/* Recent Orders */}
          <div className="bg-white rounded-xl shadow-soft border border-neutral-200">
            <div className="p-6 border-b border-neutral-200 bg-gradient-to-r from-primary-50 to-white">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-neutral-900">Recent Orders</h2>
                <FileText className="w-5 h-5 text-primary-600" />
              </div>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {recentOrders.map((order) => (
                  <div key={order.id} className="flex items-center justify-between p-3 bg-gradient-to-r from-neutral-50 to-white rounded-lg border border-neutral-200 hover:shadow-sm transition-all">
                    <div>
                      <p className="font-medium text-neutral-900">{order.type} Order</p>
                      <p className="text-sm text-neutral-600">{order.items?.length || 0} items</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        order.status === 'completed' 
                          ? 'bg-success-100 text-success-800'
                          : 'bg-warning-100 text-warning-800'
                      }`}>
                        {order.status}
                      </span>
                      <Link
                        to={`/orders/${order.id}`}
                        className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                      >
                        View
                      </Link>
                    </div>
                  </div>
                ))}
                {recentOrders.length === 0 && (
                  <p className="text-neutral-500 text-center py-4">No recent orders</p>
                )}
              </div>
            </div>
          </div>

          {/* Recent Prescriptions */}
          <div className="bg-white rounded-xl shadow-soft border border-neutral-200">
            <div className="p-6 border-b border-neutral-200 bg-gradient-to-r from-primary-50 to-white">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-neutral-900">Recent Prescriptions</h2>
                <FileText className="w-5 h-5 text-primary-600" />
              </div>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {recentPrescriptions.map((prescription) => (
                  <div key={prescription.id} className="flex items-center justify-between p-3 bg-gradient-to-r from-neutral-50 to-white rounded-lg border border-neutral-200 hover:shadow-sm transition-all">
                    <div>
                      <p className="font-medium text-neutral-900">Patient {prescription.patientId}</p>
                      <p className="text-sm text-neutral-600">{prescription.items?.length || 0} medications</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Link
                        to={`/doctor/prescriptions/${prescription.id}`}
                        className="text-primary-600 hover:text-primary-700 text-sm font-medium flex items-center space-x-1"
                      >
                        <Eye className="w-4 h-4" />
                        <span>View</span>
                      </Link>
                      <Link
                        to={`/consultations/prescriptions/${prescription.id}/print`}
                        className="text-success-600 hover:text-success-700 text-sm font-medium flex items-center space-x-1"
                      >
                        <Printer className="w-4 h-4" />
                        <span>Print</span>
                      </Link>
                    </div>
                  </div>
                ))}
                {recentPrescriptions.length === 0 && (
                  <p className="text-neutral-500 text-center py-4">No recent prescriptions</p>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Today's Appointments */}
          <div className="bg-white rounded-xl shadow-soft border border-neutral-200">
            <div className="p-6 border-b border-neutral-200 bg-gradient-to-r from-primary-50 to-white">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-neutral-900">Today's Appointments</h2>
                <Clock className="w-5 h-5 text-primary-600" />
              </div>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {recentAppointments.map((appointment) => (
                  <div key={appointment.id} className="flex items-center justify-between p-3 bg-gradient-to-r from-neutral-50 to-white rounded-lg border border-neutral-200 hover:shadow-sm transition-all">
                    <div>
                      <p className="font-medium text-neutral-900">{appointment.patient?.name}</p>
                      <p className="text-sm text-neutral-600">{appointment.doctor?.name}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-neutral-900">{appointment.start_time}</p>
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        appointment.status === 'confirmed' 
                          ? 'bg-success-100 text-success-800'
                          : appointment.status === 'scheduled'
                          ? 'bg-warning-100 text-warning-800'
                          : 'bg-error-100 text-error-800'
                      }`}>
                        {appointment.status}
                      </span>
                    </div>
                  </div>
                ))}
                {recentAppointments.length === 0 && (
                  <div className="text-center py-8">
                    <Calendar className="w-12 h-12 text-neutral-300 mx-auto mb-3" />
                    <p className="text-neutral-500 mb-3">No appointments scheduled for today</p>
                    <Link to="/appointments" className="text-primary-600 hover:text-primary-700 text-sm font-medium">
                      Schedule an appointment →
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-xl shadow-soft border border-neutral-200">
            <div className="p-6 border-b border-neutral-200 bg-gradient-to-r from-primary-50 to-white">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-neutral-900">Quick Actions</h2>
                <Activity className="w-5 h-5 text-primary-600" />
              </div>
            </div>
            <div className="p-6">
              <div className="space-y-3">
                <Link to="/appointments" className="w-full flex items-center justify-between p-4 text-left bg-gradient-to-r from-primary-50 to-primary-100 hover:from-primary-100 hover:to-primary-200 rounded-lg transition-all duration-200 group border border-primary-200">
                  <div>
                    <span className="font-medium text-primary-900">Schedule New Appointment</span>
                    <p className="text-sm text-primary-700 mt-0.5">Book patient visit</p>
                  </div>
                  <Calendar className="w-5 h-5 text-primary-600 group-hover:scale-110 transition-transform" />
                </Link>
                <Link to="/patients" className="w-full flex items-center justify-between p-4 text-left bg-gradient-to-r from-success-50 to-success-100 hover:from-success-100 hover:to-success-200 rounded-lg transition-all duration-200 group border border-success-200">
                  <div>
                    <span className="font-medium text-success-900">Add New Patient</span>
                    <p className="text-sm text-success-700 mt-0.5">Register patient</p>
                  </div>
                  <Users className="w-5 h-5 text-success-600 group-hover:scale-110 transition-transform" />
                </Link>
                <Link to="/reports" className="w-full flex items-center justify-between p-4 text-left bg-gradient-to-r from-secondary-50 to-secondary-100 hover:from-secondary-100 hover:to-secondary-200 rounded-lg transition-all duration-200 group border border-secondary-200">
                  <div>
                    <span className="font-medium text-secondary-900">Generate Report</span>
                    <p className="text-sm text-secondary-700 mt-0.5">View analytics</p>
                  </div>
                  <TrendingUp className="w-5 h-5 text-secondary-600 group-hover:scale-110 transition-transform" />
                </Link>
                <Link to="/billing" className="w-full flex items-center justify-between p-4 text-left bg-gradient-to-r from-accent-50 to-accent-100 hover:from-accent-100 hover:to-accent-200 rounded-lg transition-all duration-200 group border border-accent-200">
                  <div>
                    <span className="font-medium text-accent-900">Process Billing</span>
                    <p className="text-sm text-accent-700 mt-0.5">Manage payments</p>
                  </div>
                  <CreditCard className="w-5 h-5 text-accent-600 group-hover:scale-110 transition-transform" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Doctor Quick Actions */}
      {isDoctorRole && (
        <div className="bg-white rounded-xl shadow-soft border border-neutral-200">
          <div className="p-6 border-b border-neutral-200 bg-gradient-to-r from-primary-50 to-white">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-neutral-900">Quick Actions</h2>
              <Activity className="w-5 h-5 text-primary-600" />
            </div>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Link
                to="/consultations/new"
                className="flex items-center justify-between p-4 text-left bg-gradient-to-r from-primary-50 to-primary-100 hover:from-primary-100 hover:to-primary-200 rounded-lg transition-all duration-200 group border border-primary-200"
              >
                <div>
                  <span className="font-medium text-primary-900">Start Next Consultation</span>
                  <p className="text-sm text-primary-700">Begin patient consultation</p>
                </div>
                <Play className="w-6 h-6 text-primary-600 group-hover:scale-110 transition-transform" />
              </Link>
              <Link
                to="/doctor/prescriptions/new"
                className="flex items-center justify-between p-4 text-left bg-gradient-to-r from-success-50 to-success-100 hover:from-success-100 hover:to-success-200 rounded-lg transition-all duration-200 group border border-success-200"
              >
                <div>
                  <span className="font-medium text-success-900">New Prescription</span>
                  <p className="text-sm text-success-700">Write prescription</p>
                </div>
                <FileText className="w-6 h-6 text-success-600 group-hover:scale-110 transition-transform" />
              </Link>
              <Link
                to="/orders/new"
                className="flex items-center justify-between p-4 text-left bg-gradient-to-r from-secondary-50 to-secondary-100 hover:from-secondary-100 hover:to-secondary-200 rounded-lg transition-all duration-200 group border border-secondary-200"
              >
                <div>
                  <span className="font-medium text-secondary-900">Place Lab Order</span>
                  <p className="text-sm text-secondary-700">Order lab tests</p>
                </div>
                <Plus className="w-6 h-6 text-secondary-600 group-hover:scale-110 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      )}

        {/* System Alerts */}
        {!isDoctorRole && (
          <div className="bg-white rounded-xl shadow-soft border border-neutral-200 p-6 mt-6">
            <div className="flex items-center mb-4">
              <AlertCircle className="w-5 h-5 text-warning-500 mr-2" />
              <h2 className="text-lg font-semibold text-neutral-900">System Alerts</h2>
            </div>
            <div className="space-y-3">
              <div className="flex items-center p-4 bg-gradient-to-r from-warning-50 to-warning-100 border border-warning-200 rounded-lg">
                <AlertCircle className="w-5 h-5 text-warning-600 mr-3 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-warning-900">Backup Reminder</p>
                  <p className="text-sm text-warning-700">Daily backup scheduled for 11:00 PM tonight</p>
                </div>
              </div>
              <div className="flex items-center p-4 bg-gradient-to-r from-primary-50 to-primary-100 border border-primary-200 rounded-lg">
                <CheckCircle className="w-5 h-5 text-primary-600 mr-3 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-primary-900">System Update</p>
                  <p className="text-sm text-primary-700">New features available in version 2.1.0</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
